/**
 * Compute the current sale status based on sale data.
 * This is the SINGLE SOURCE OF TRUTH for status calculation.
 * Used by both front-end (SaleStatus.js) and back-end (functions/saleStatusUtil.js).
 *
 * IMPORTANT: This file must be kept in sync with functions/saleStatusUtil.js
 * Both files contain identical logic - one for ES modules (front-end),
 * one for CommonJS (back-end/Cloud Functions).
 *
 * @param {Object} sale - The sale document data
 * @param {Object} sale.status - Status object with child document statuses
 * @param {Object} sale.total - Totals object with items, screens, hardware counts
 * @param {Object} sale.terms - Payment terms
 * @param {boolean} sale.holdScreens - Whether screens are on hold
 * @param {boolean} sale.holdHardware - Whether hardware is on hold
 * @param {boolean} sale.requiresPunchOut - Whether punch out is required
 * @param {number} sale.downPaymentAmount - Amount of down payment received
 * @param {Object} [sale.shipTo] - Ship to address (used to detect pickup sales)
 * @param {Object} [options] - Optional overrides
 * @param {boolean} [options.hasPendingChangeOrder] - Override for pending change order check
 * @param {boolean} [options.isAwaitingDownPayment] - Override for down payment check (front-end uses computed property)
 * @returns {string} The computed status type
 */
export function computeSaleStatus(sale, options = {}) {
  const {
    status,
    total,
    terms,
    holdScreens,
    holdHardware,
    requiresPunchOut,
    shipTo,
  } = sale;
  const downPaymentAmount = sale.downPaymentAmount || 0;

  // Canceled is a terminal status — never recompute away from it
  if (sale.canceled) {
    return "canceled";
  }

  // Check for pending change order
  const hasPendingChangeOrder =
    options.hasPendingChangeOrder !== undefined
      ? options.hasPendingChangeOrder
      : Object.values(status.changeOrders || {}).includes("pending");
  if (hasPendingChangeOrder) {
    return "changeOrder";
  }

  // Check down payment
  // Only "awaiting" if NO down payment has been made yet and some is due
  // Skip if sale balance is zero or negative (paid in full without down payment)
  const downPaymentRequired =
    terms && terms.cashSale && total.price > 0
      ? (total.price * terms.downPayment) / 100
      : 0;
  const isAwaitingDownPayment =
    options.isAwaitingDownPayment !== undefined
      ? options.isAwaitingDownPayment
      : !downPaymentAmount && downPaymentRequired > 0 && total.balance > 0;
  if (isAwaitingDownPayment) {
    return status.downPaymentRequested ? "downPaymentRequested" : "downPayment";
  }

  // Check ordering status
  const isFullyOrdered = Object.values(status.vendors || {}).every(
    (vendor) => vendor.fullyOrdered
  );
  if (!isFullyOrdered) {
    return "ordering";
  }

  // Check acknowledgement status
  const isFullyAcked = Object.values(status.orders || {}).every(
    (order) => order.fullyAcked
  );
  if (!isFullyAcked) {
    return "ack";
  }

  // Check for critical issues
  const hasUnresolvedCriticalIssue = Object.values(status.issues || {}).some(
    (issue) => !issue.isResolved && issue.isCritical
  );
  if (hasUnresolvedCriticalIssue) {
    return "issuesCritical";
  }

  // Item-based statuses (also applies to labor-only sales with installation)
  if (total.items > 0 || ((status.install || {}).totalLabors || 0) > 0) {
    // Skip orphan acks/bills/packing-slips whose order has been deleted
    // (status.orders is cleaned by DELETE_ORDER, but legacy entries in
    // status.acks/bills/packingSlips can otherwise block status advancement).
    // Entries with a missing `orderID` are also treated as orphan — every
    // well-formed entry is written with `orderID` by SAVE_ACK / SAVE_BILL /
    // SAVE_PACKING_SLIP, so a missing `orderID` indicates the entry was
    // recreated via a Firestore dot-path field update (e.g. `status.acks.X
    // .fullyBilled = true` after the parent ack was deleted). Sale
    // 03900066B reproduced this with a `{fullyBilled: true}` orphan that
    // pinned the sale at "production".
    const orders = status.orders || {};
    const isLiveChild = (entry) =>
      entry && entry.orderID && entry.orderID in orders;

    // An ack is only received once QC makes its items ready, so a truck saved
    // with the whole order on it used to leave the sale "In production" until
    // QC -- the "Quality Control" status below was unreachable. A live slip
    // that says the order's receipt is complete (`receivedComplete`) and still
    // waits for QC puts that order past production. A partial receipt does
    // not: the vendor still owes the rest.
    const packingSlips = Object.values(status.packingSlips || {}).filter(
      isLiveChild
    );
    const awaitingQC = new Set(
      packingSlips
        .filter((ps) => ps.receivedComplete === true && !ps.qcInspected)
        .map((ps) => ps.orderID)
    );

    const isFullyReceived = Object.values(status.acks || {})
      .filter(isLiveChild)
      .every((ack) => ack.fullyReceived || awaitingQC.has(ack.orderID));
    if (!isFullyReceived) {
      return "production";
    }

    const isQCInspected = packingSlips.every((ps) => ps.qcInspected);
    if (!isQCInspected) {
      return "qc";
    }

    const isPickupSale = Boolean(shipTo && shipTo.pickup);
    const pickup = status.pickup || {};
    const delivery = status.delivery || {};
    const install = status.install || {};

    const isFullyScreenScheduled = isPickupSale
      ? (pickup.screensScheduled || 0) + (pickup.screensComplete || 0) >=
        total.screens
      : (delivery.screensScheduled || 0) >= total.screens ||
        (delivery.screensComplete || 0) >= total.screens;
    const isFullyHardwareScheduled = isPickupSale
      ? (pickup.hardwareScheduled || 0) + (pickup.hardwareComplete || 0) >=
        total.hardware
      : (delivery.hardwareScheduled || 0) >= total.hardware ||
        (delivery.hardwareComplete || 0) >= total.hardware;
    const isFullyInstalled =
      (install.completeLabors || 0) >= (install.totalLabors || 0);

    const isFullyItemsScheduled = isPickupSale
      ? (pickup.scheduled || 0) + (pickup.complete || 0) >= total.items
      : (delivery.scheduled || 0) >= total.items ||
        (delivery.complete || 0) >= total.items ||
        (install.readyItems || 0) >= total.items;

    // Child accessories (`total.accessories`) are separate stock that must be
    // scheduled and delivered on its own, but it is excluded from
    // `total.items`, so without these gates a sale whose units are all
    // delivered reads "fully delivered" while a ready accessory is still
    // sitting in the warehouse (sale 01000033A, and 6 others).
    const totalAccessories = total.accessories || 0;
    const isFullyAccessoriesScheduled = isPickupSale
      ? (pickup.accessoriesScheduled || 0) +
          (pickup.accessoriesComplete || 0) >=
        totalAccessories
      : (delivery.accessoriesScheduled || 0) >= totalAccessories ||
        (delivery.accessoriesComplete || 0) >= totalAccessories;
    const isFullyAccessoriesDelivered = isPickupSale
      ? (pickup.accessoriesComplete || 0) >= totalAccessories
      : (delivery.accessoriesComplete || 0) >= totalAccessories;

    const hasInstallNeedsReschedule = (install.needsReschedule || 0) > 0;
    const hasDeliveryNeedsReschedule = (delivery.needsReschedule || 0) > 0;
    const isFullyScheduled =
      isFullyItemsScheduled &&
      (isFullyInstalled ||
        (install.scheduledHours || 0) >= (install.totalHours || 0)) &&
      (holdScreens || isFullyScreenScheduled) &&
      (holdHardware || isFullyHardwareScheduled) &&
      isFullyAccessoriesScheduled &&
      !hasInstallNeedsReschedule &&
      !hasDeliveryNeedsReschedule;
    if (!isFullyScheduled) {
      // Cash sales are scheduled like everything else once their material is
      // received — the outstanding balance is tracked separately (see the
      // ScheduledUnpaidCashSales widget) and no longer blocks scheduling.
      return "scheduling";
    }

    const isFullyScreenDelivered = isPickupSale
      ? (pickup.screensComplete || 0) >= total.screens
      : (delivery.screensComplete || 0) >= total.screens;
    const isFullyHardwareDelivered = isPickupSale
      ? (pickup.hardwareComplete || 0) >= total.hardware
      : (delivery.hardwareComplete || 0) >= total.hardware;
    const isFullyDelivered =
      isFullyAccessoriesDelivered &&
      (isPickupSale
        ? (pickup.complete || 0) >= total.items &&
          (holdScreens || isFullyScreenDelivered) &&
          (holdHardware || isFullyHardwareDelivered)
        : (delivery.complete || 0) >= total.items &&
          (holdScreens || isFullyScreenDelivered) &&
          (holdHardware || isFullyHardwareDelivered));
    if (!isFullyDelivered) {
      if (isPickupSale) {
        const hasItemsAtPickupLocation =
          (pickup.ready || 0) > 0 ||
          (pickup.screensReady || 0) > 0 ||
          (pickup.hardwareReady || 0) > 0 ||
          (pickup.accessoriesReady || 0) > 0;
        return hasItemsAtPickupLocation ? "pickup" : "scheduled";
      }
      return "scheduled";
    }

    const isInstalling =
      (install.completeItems || 0) > 0 &&
      (install.completeLabors || 0) < (install.totalLabors || 0);
    if (isInstalling) {
      return "installing";
    }

    if (!isFullyInstalled) {
      return "install";
    }

    // Screens and hardware are SERVICE items. When they are held they sit in
    // our warehouse until the customer asks for them, which is routinely long
    // after the job is otherwise finished — so they must not park the sale in
    // a pre-billing phase. Doing so stalled the sale short of `service`
    // forever, which in turn stalled the salesperson's commission (that
    // unlocks at `service`/`complete`) and kept finished projects looking
    // active.
    //
    // Unheld screens/hardware never reach this point: `isFullyScheduled` and
    // `isFullyDelivered` above already hold the sale at "scheduling" /
    // "scheduled" for those. So everything still outstanding here is held,
    // and held items belong to Service — see the `service` return below,
    // which this feeds. `schedulingMisc` / `scheduledMisc` are consequently
    // no longer produced, and their SALE_STATUS_TYPES entries have been
    // removed now that the 15 sales still storing one have been repaired
    // (scripts/repair-legacy-misc-statuses.mjs).
    const hasOutstandingServiceItems =
      !isFullyScreenScheduled ||
      !isFullyHardwareScheduled ||
      !isFullyScreenDelivered ||
      !isFullyHardwareDelivered;

    const isFullyBilled = Object.values(status.acks || {})
      .filter(isLiveChild)
      .every((ack) => ack.fullyBilled);
    if (!isFullyBilled) {
      return "billing";
    }

    const isFullyPaid =
      isFullyBilled &&
      Object.values(status.bills || {})
        .filter(isLiveChild)
        .every((bill) => bill.fullyPaid);
    if (!isFullyPaid) {
      return "paying";
    }

    // Use balance check to handle voided invoices correctly
    if (total.balance > 0) {
      return "collecting";
    }

    // Billing, vendor payment and the customer balance are all genuinely
    // outstanding work and still come first. Once they are clear, a job with
    // punch-out left OR held screens/hardware left is exactly what "Service"
    // means: finished and in service, with items stored for the customer.
    const isPunchOutComplete =
      !total.items || !requiresPunchOut || status.punchOutComplete;
    if (!isPunchOutComplete || hasOutstandingServiceItems) {
      return "service";
    }
  }

  // Final statuses
  const hasUnresolvedIssue = Object.values(status.issues || {}).some(
    (issue) => !issue.isResolved
  );
  if (hasUnresolvedIssue) {
    return "issues";
  }

  return "complete";
}
